// Local mirror of `@docplanner/browser-observability` (spec:
// https://github.com/DocPlanner/docplanner/blob/main/openspec/specs/frontend-observability/spec.md).
// We can't depend on the upstream lib here because it ships a hardcoded
// `@datadog/browser-rum-react` plugin in its `init.ts:42`, which breaks Vue.
// API kept 1:1 with the spec so this file becomes a drop-in replacement
// (delete this dir + `yarn add @docplanner/browser-observability`) once the
// upstream lib accepts non-React frameworks.
import { datadogRum } from "@datadog/browser-rum";
import { datadogLogs } from "@datadog/browser-logs";
import type { ObservabilityConfig, ObservabilityUser } from "./types";
import { configureForbiddenKeys, stripPii } from "./pii";
import { redactPii } from "./redact";

const FIXED_SESSION_REPLAY_SAMPLE_RATE = 0;
const FIXED_TELEMETRY_SAMPLE_RATE = 20;
const DEFAULT_SESSION_SAMPLE_RATE = 10;
const DEFAULT_SITE = "datadoghq.eu";

let rumInitialized = false;
let logsInitialized = false;

export function initObservability(config: ObservabilityConfig): void {
  if (rumInitialized && logsInitialized) return;

  if (!config.applicationId || !config.clientToken) {
    if (typeof console !== "undefined") {
      console.warn(
        "[browser-observability] Missing applicationId or clientToken — skipping initialization."
      );
    }
    return;
  }

  configureForbiddenKeys(config.additionalForbiddenKeys);

  try {
    datadogRum.init({
      applicationId: config.applicationId,
      clientToken: config.clientToken,
      site: config.site ?? DEFAULT_SITE,
      env: config.env,
      service: config.service,
      version: config.version,
      sessionSampleRate: config.sessionSampleRate ?? DEFAULT_SESSION_SAMPLE_RATE,
      sessionReplaySampleRate: FIXED_SESSION_REPLAY_SAMPLE_RATE,
      telemetrySampleRate: FIXED_TELEMETRY_SAMPLE_RATE,
      defaultPrivacyLevel: "mask",
      allowedTracingUrls: config.allowedTracingUrls ?? [],
      trackUserInteractions: true,
      trackResources: true,
      trackLongTasks: true,
      beforeSend: (event) => {
        // Regex-based PII scrubbing on string fields that may carry embedded
        // patient data (URL query params, error messages, action names, etc.).
        // Complements `defaultPrivacyLevel: 'mask'` (DOM-level) and
        // `additionalForbiddenKeys` (log key-level).
        const e = event as Record<string, any>;
        if (e.view?.url) e.view.url = redactPii(e.view.url);
        if (e.view?.referrer) e.view.referrer = redactPii(e.view.referrer);
        if (e.error?.message) e.error.message = redactPii(e.error.message);
        if (e.action?.target?.name) e.action.target.name = redactPii(e.action.target.name);
        if (e.resource?.url) e.resource.url = redactPii(e.resource.url);
        return true;
      },
    });
    rumInitialized = true;
  } catch (error) {
    if (typeof console !== "undefined") {
      console.warn("[browser-observability] RUM initialization failed:", error);
    }
  }

  try {
    datadogLogs.init({
      clientToken: config.clientToken,
      site: config.site ?? DEFAULT_SITE,
      env: config.env,
      service: config.service,
      version: config.version,
      forwardErrorsToLogs: true,
      forwardConsoleLogs: ["error"],
      sessionSampleRate: 100,
      beforeSend: (event) => {
        // 1. Strip forbidden keys from log context (e.g. cpf, summary).
        if (event.context) {
          event.context = stripPii(
            event.context as Record<string, unknown>
          ) as typeof event.context;
        }
        // 2. Regex-scrub PII embedded in the log message itself.
        if (typeof event.message === "string") {
          event.message = redactPii(event.message);
        }
        return true;
      },
    });
    logsInitialized = true;
  } catch (error) {
    if (typeof console !== "undefined") {
      console.warn("[browser-observability] Logs initialization failed:", error);
    }
  }
}

export function setObservabilityUser(user: ObservabilityUser): void {
  if (!rumInitialized) return;
  datadogRum.setUser({ id: user.id, ...(user.role ? { role: user.role } : {}) });
}

export function clearObservabilityUser(): void {
  if (!rumInitialized) return;
  datadogRum.clearUser();
}

export function isObservabilityInitialized(): boolean {
  return rumInitialized && logsInitialized;
}
